import React, { useState, useEffect } from 'react'
import type { Place, UpdatePlaceParams } from '../api/place'
import { adminApi } from '../api'
import { toastUtil } from '../../utils/toast'
import { useCategoryStore } from '../../stores/categoryIconStore'
import { formatScoreOneDecimal } from '../../utils/scoreFormat'

interface AdminPlaceDetailModalProps {
  place: Place
  onClose: () => void
  formatDate: (dateString: string) => string
  formatCategory: (category: string) => string
  formatSubCategory: (subCategory: string | null) => string
  onUpdate?: (updatedPlace: Place) => void
  onDelete?: () => void
  onDeleteSuccess?: () => void
}

const AdminPlaceDetailModal: React.FC<AdminPlaceDetailModalProps> = ({
  place,
  onClose,
  formatDate,
  formatCategory,
  formatSubCategory,
  onUpdate,
  onDelete,
  onDeleteSuccess,
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [formData, setFormData] = useState<UpdatePlaceParams>({
    name: place.name,
    category: place.category,
    lat: place.lat,
    lng: place.lng,
    address: place.address || '',
    phone_number: place.phone_number || '',
    is_recommended: place.is_recommended,
    sub_category: place.sub_category_override || '',
  })
  const [isLoading, setIsLoading] = useState(false)

  // place prop이 변경되면 formData 업데이트
  useEffect(() => {
    if (!isEditMode) {
      setFormData({
        name: place.name,
        category: place.category,
        lat: place.lat,
        lng: place.lng,
        address: place.address || '',
        phone_number: place.phone_number || '',
        is_recommended: place.is_recommended ?? false,
        sub_category: place.sub_category_override || '',
      })
    }
  }, [place, isEditMode])

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toastUtil.error('장소명을 입력해주세요')
      return
    }

    try {
      setIsLoading(true)
      const response = await adminApi.updatePlace(place.id, formData)
      toastUtil.success('장소 정보가 수정되었습니다')
      setIsEditMode(false)
      onUpdate?.(response.data)
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        '장소 정보 수정 중 오류가 발생했습니다.'
      toastUtil.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      name: place.name,
      category: place.category,
      lat: place.lat,
      lng: place.lng,
      address: place.address || '',
      phone_number: place.phone_number || '',
      is_recommended: place.is_recommended,
      sub_category: place.sub_category_override || '',
    })
    setIsEditMode(false)
  }

  const handleToggleRecommended = async () => {
    try {
      setIsLoading(true)
      const response = await adminApi.updatePlaceRecommended(
        place.id,
        !(place.is_recommended ?? false)
      )
      toastUtil.success(response.message)
      onUpdate?.({ ...place, is_recommended: response.data.is_recommended })
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        '선정 여부 수정 중 오류가 발생했습니다.'
      toastUtil.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditClick = () => {
    setIsEditMode(true)
  }

  const handleDeleteClick = async () => {
    const message = `정말 "${place.name}" 장소를 삭제하시겠습니까?\n\n삭제 시 다음 데이터도 함께 삭제됩니다:\n• 리뷰 (reviews)\n• 리뷰 댓글 (review_comments)\n• 리뷰 좋아요 (review_likes)\n• 리뷰 신고 (review_reports)\n• 리뷰 태그 (review_tagged_users)\n• 리뷰 키워드 (review_connected_keywords)\n• 영업시간 (place_business_hours)\n• 북마크 관계 (bookmark_category_place)\n• 비교 히스토리 (place_comparison_histories)\n• 관련 알림 (notifications)\n\n삭제된 데이터는 복구할 수 없습니다.`

    if (!window.confirm(message)) return

    try {
      setIsLoading(true)
      await adminApi.deletePlace(place.id)
      toastUtil.success('장소가 삭제되었습니다')
      onDeleteSuccess?.()
      onClose()
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        '장소 삭제 중 오류가 발생했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="p-24px">
      <div className="grid grid-cols-2 gap-16px mb-24px">
        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            ID
          </label>
          <div className="text-style-body2 text-textColorText2">{place.id}</div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            카카오 플레이스 ID
          </label>
          <div className="text-style-body2 text-textColorText2 break-words">
            {place.kakao_place_id}
          </div>
        </div>

        <div className="col-span-2">
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            장소명
          </label>
          {isEditMode ? (
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="장소명을 입력하세요"
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
                placeholder:text-textColorText2
              `}
            />
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {place.name}
            </div>
          )}
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            카테고리
          </label>
          {isEditMode ? (
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  category: e.target.value,
                })
              }
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
              `}
            >
              {useCategoryStore.getState().getAllCategories().map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {formatCategory(place.category)}
            </div>
          )}
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            서브 카테고리
          </label>
          {isEditMode ? (
            <>
              {/*
                카카오 분류가 틀릴 때가 있어서 직접 지정할 수 있게 열어둔다.
                예: 광주 '사시사철'은 카페인데 카카오는 '음식점 > 양식 > 피자'로 준다.
                비우면 다시 카카오 원본을 따라간다.
              */}
              <div className="flex gap-8px">
                <input
                  type="text"
                  value={formData.sub_category ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, sub_category: e.target.value })
                  }
                  placeholder="비우면 카카오 분류를 따라갑니다 (예: 카페, 한식)"
                  className={`
                    flex-1
                    min-w-0
                    py-8px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                    placeholder:text-textColorText3
                  `}
                />
                {/*
                  카카오 분류가 뭉뚱그려질 때(막국수집이 '국수'로 나오는 식)
                  네이버 쪽 분류가 더 구체적이다. 새 창으로 열어 눈으로 확인하고
                  돌아와 입력하라는 뜻이다. 자동으로 가져오지는 않는다.
                */}
                <a
                  href={`https://map.naver.com/p/search/${encodeURIComponent(
                    `${place.name} ${place.address ?? ''}`.trim()
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 flex items-center px-14px rounded-8px border border-brandPrimary-20% text-style-button2 text-textColorText2 hover:bg-4B3621-80%-5%"
                >
                  네이버에서 확인
                </a>
              </div>
              <p className="text-style-labelS text-textColorText3 mt-4px">
                지도 바텀시트·랭킹 목록에 보이는 라벨입니다. 추천 피드의 취향
                분류에도 쓰입니다. 카카오가 뭉뚱그릴 때는 네이버에서 확인해
                직접 적어주세요.
              </p>
            </>
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {formatSubCategory(place.sub_category)}
              {!place.sub_category_override && (
                <span className="text-style-labelS text-textColorText3 ml-8px">
                  (카카오 분류 따름)
                </span>
              )}
            </div>
          )}
        </div>

        {/* 카카오 원본 세부 카테고리 (읽기 전용, 카카오 등록 시 자동 저장) */}
        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            카카오 카테고리
          </label>
          <div className="text-style-body2 text-textColorText2">
            {place.kakao_category || '-'}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            위도
          </label>
          {isEditMode ? (
            <input
              type="text"
              value={formData.lat}
              onChange={(e) =>
                setFormData({ ...formData, lat: e.target.value })
              }
              placeholder="위도를 입력하세요"
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
                placeholder:text-textColorText2
              `}
            />
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {place.lat}
            </div>
          )}
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            경도
          </label>
          {isEditMode ? (
            <input
              type="text"
              value={formData.lng}
              onChange={(e) =>
                setFormData({ ...formData, lng: e.target.value })
              }
              placeholder="경도를 입력하세요"
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
                placeholder:text-textColorText2
              `}
            />
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {place.lng}
            </div>
          )}
        </div>

        <div className="col-span-2">
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            주소
          </label>
          {isEditMode ? (
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              placeholder="주소를 입력하세요"
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
                placeholder:text-textColorText2
              `}
            />
          ) : (
            <div className="text-style-body2 text-textColorText2 break-words">
              {place.address || '-'}
            </div>
          )}
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            전화번호
          </label>
          {isEditMode ? (
            <input
              type="tel"
              value={formData.phone_number || ''}
              onChange={(e) =>
                setFormData({ ...formData, phone_number: e.target.value })
              }
              placeholder="전화번호를 입력하세요"
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
                placeholder:text-textColorText2
              `}
            />
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {place.phone_number || '-'}
            </div>
          )}
        </div>

        {isEditMode && (
          <div className="col-span-2 flex items-center gap-8px">
            <input
              type="checkbox"
              id="is_recommended"
              checked={formData.is_recommended === true}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  is_recommended: e.target.checked,
                })
              }
              className="w-16px h-16px rounded border-brandPrimary-20%"
            />
            <label
              htmlFor="is_recommended"
              className="text-style-body2 text-textColorText2"
            >
              선정 (추천)
            </label>
          </div>
        )}

        {!isEditMode && (
          <>
            <div>
              <label className="text-style-labelS text-textColorText2 mb-4px block">
                평점
              </label>
              <div className="text-style-body2 text-textColorText2">
                {place.rating != null && place.rating !== ''
                  ? formatScoreOneDecimal(place.rating)
                  : '-'}
              </div>
            </div>

            <div>
              <label className="text-style-labelS text-textColorText2 mb-4px block">
                리뷰 수
              </label>
              <div className="text-style-body2 text-textColorText2">
                {place.user_ratings_total?.toLocaleString() || '-'}
              </div>
            </div>
          </>
        )}
      </div>

      {!isEditMode && (
        <div className="grid grid-cols-2 gap-16px pt-16px border-t border-brandPrimary-5%">
          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              등록일
            </label>
            <div className="text-style-body2 text-textColorText2">
              {formatDate(place.created_at)}
            </div>
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              수정일
            </label>
            <div className="text-style-body2 text-textColorText2">
              {formatDate(place.updated_at)}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-12px mt-24px pt-24px border-t border-brandPrimary-5%">
        {isEditMode ? (
          <>
            <button
              onClick={handleCancel}
              className={`
                py-10px
                px-20px
                border
                border-brandPrimary-20%
                text-textColorText2
                rounded-8px
                text-style-button2
                hover:bg-backgroundColorBg2
                transition-colors
              `}
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.name.trim() || isLoading}
              className={`
                py-10px
                px-20px
                bg-4B3621-80%
                text-white
                rounded-8px
                text-style-button2
                hover:bg-4B3621-80%-70%
                transition-colors
                disabled:bg-4B3621-80%-10%
                disabled:text-textColorText3
                disabled:cursor-not-allowed
                min-w-[120px]
              `}
            >
              {isLoading ? '수정 중...' : '수정 완료'}
            </button>
          </>
        ) : (
          <>
            {onDelete && (
              <button
                onClick={handleDeleteClick}
                disabled={isLoading}
                className={`
                  py-10px
                  px-20px
                  bg-semanticError
                  text-white
                  rounded-8px
                  text-style-button2
                  hover:bg-semanticError-70%
                  transition-colors
                  disabled:bg-semanticError-10%
                  disabled:text-textColorText3
                  disabled:cursor-not-allowed
                `}
              >
                삭제
              </button>
            )}
            {onUpdate && (
              <button
                onClick={handleEditClick}
                className={`
                  py-10px
                  px-20px
                  bg-4B3621-80%
                  text-white
                  rounded-8px
                  text-style-button2
                  hover:bg-4B3621-80%-70%
                  transition-colors
                `}
              >
                수정
              </button>
            )}
            <button
              onClick={handleToggleRecommended}
              disabled={isLoading}
              className={`
                py-10px
                px-20px
                rounded-8px
                text-style-button2
                transition-colors
                disabled:opacity-50
                disabled:cursor-not-allowed
                ${
                  (place.is_recommended ?? false)
                    ? 'border border-brandPrimary-20% text-brown-80%-main hover:bg-4B3621-80%-5%'
                    : 'bg-4B3621-80%-10% text-brown-80%-main hover:bg-4B3621-80%-20%'
                }
              `}
            >
              {(place.is_recommended ?? false) ? '선정 해제' : '선정'}
            </button>
            <button
              onClick={onClose}
              className={`
                py-10px
                px-20px
                border
                border-brandPrimary-20%
                text-textColorText2
                rounded-8px
                text-style-button2
                hover:bg-backgroundColorBg2
                transition-colors
              `}
            >
              닫기
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default AdminPlaceDetailModal
